import { useState, useEffect } from 'react';
import { useAppStore } from '../stores/app';
import { useGameEvents } from '../hooks/useGameEvents';
import { isVK } from '../platform/detect';
import { usePurchaseStore } from '../stores/purchase';

export function LobbyPage() {
  const room = useAppStore((s) => s.room);
  const userId = useAppStore((s) => s.userId);
  const error = useAppStore((s) => s.error);
  const setError = useAppStore((s) => s.setError);

  const [topic, setTopic] = useState('');
  const [questionsCount, setQuestionsCount] = useState(10);
  const [timePerQuestion, setTimePerQuestion] = useState(50);
  const [resultsDisplayTime, setResultsDisplayTime] = useState(12);
  const [gameMode, setGameMode] = useState<'quiz' | 'truefalse'>('quiz');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [isStarting, setIsStarting] = useState(false);
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [funFact, setFunFact] = useState(0);

  const { setReady, startGame, leaveRoom } = useGameEvents();
  const openPurchase = usePurchaseStore((s) => s.open);

  // ⚠️ ИСПРАВЛЕНИЕ: Сбрасываем isStarting при ошибке
  useEffect(() => {
    if (error) {
      setIsStarting(false);

      // Если тема запрещена - очищаем её
      if (error === 'TOPIC_NOT_ALLOWED') {
        setTopic('');
      }

      // В VK при NO_GAMES_LEFT — сразу открываем модалку покупки
      if (error === 'NO_GAMES_LEFT' && isVK()) {
        openPurchase();
      }
    }
  }, [error, openPurchase]);

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  const isHost = room.hostId === userId;
  const myPlayer = room.players.find(p => p.id === userId);
  const readyCount = room.players.filter(p => p.isReady).length;
  const canStart = room.players.length >= 1 && topic.trim().length > 0;

  const handleStart = () => {
    if (!canStart) return;
    setIsStarting(true);
    // Очищаем предыдущую ошибку
    setError(null);
    startGame({
      topic,
      totalQuestions: questionsCount,
      timePerQuestion,
      resultsDisplayTime,
      difficulty,
      gameMode,
    });
  };

  const funFacts = [
    '🤖 AI генерирует уникальные вопросы...',
    '🧠 Подбираем интересные факты...',
    '⚡ Проверяем правильность ответов...',
    '🎯 Настраиваем сложность...',
    '✨ Почти готово...',
  ];

  useEffect(() => {
    if (!isStarting) { setPrepSeconds(0); setFunFact(0); return; }
    const timer = setInterval(() => setPrepSeconds(s => s + 1), 1000);
    const factTimer = setInterval(() => setFunFact(f => f < 4 ? f + 1 : f), 4000);
    return () => { clearInterval(timer); clearInterval(factTimer); };
  }, [isStarting]);

  const handleReady = () => {
    if (myPlayer) {
      setReady(!myPlayer.isReady);
    }
  };

  if (isStarting) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white p-6 relative">
        <button onClick={leaveRoom} className="absolute top-4 left-4 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all">← Выйти</button>
        <div className="text-6xl mb-6 animate-bounce">🎲</div>
        <p className="text-2xl font-bold mb-2">Готовим вопросы</p>
        <p className="text-purple-300 mb-6 text-center transition-opacity duration-500">{funFacts[funFact]}</p>
        <div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-1000" style={{width: Math.min(prepSeconds * 8, 95) + '%'}} />
        </div>
        <p className="text-gray-500 text-sm">{prepSeconds} сек</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
      <button onClick={leaveRoom} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all mb-2">← Выйти</button>
      <header className="text-center mb-8">
        <div className="text-sm text-gray-400 mb-1">Комната</div>
        <div className="text-4xl font-bold tracking-wider text-primary-400">{room.code}</div>
      </header>

      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">
          Игроки ({room.players.length}/16)
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {room.players.map((player) => (
            <div
              key={player.id}
              className={'p-4 rounded-xl flex items-center gap-3 ' +
                (player.isReady ? 'bg-green-500/20 border border-green-500/50' : 'bg-white/10') +
                (player.id === userId ? ' ring-2 ring-primary-500' : '')}
            >
              <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-lg font-bold">
                {player.avatar || '🎮'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {player.displayName}
                  
                </div>
                <div className="text-xs text-gray-400">
                  {player.isReady ? '✓ Готов' : 'Не готов'}
                </div>
              </div>
            </div>
          ))}

          {Array.from({ length: Math.max(0, 2 - room.players.length) }).map((_, i) => (
            <div
              key={'empty-' + i}
              className="p-4 rounded-xl bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center text-gray-500"
            >
              Ожидание...
            </div>
          ))}
        </div>
      </section>

      {isHost && (
        <section className="mb-8 p-4 bg-white/10 rounded-xl">
          <h2 className="text-lg font-semibold mb-4">Настройки игры</h2>
          
          {/* Режим игры */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setGameMode("quiz")}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                gameMode === "quiz"
                  ? "bg-primary-500 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              🧠 Викторина
            </button>
            <button
              onClick={() => setGameMode("truefalse")}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                gameMode === "truefalse"
                  ? "bg-primary-500 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              🤔 Правда/Ложь
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Тема викторины</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => {
                  setTopic(e.target.value);
                  // Очищаем ошибку при вводе новой темы
                  if (error === 'TOPIC_NOT_ALLOWED') {
                    setError(null);
                  }
                }}
                placeholder="Введи ЛЮБУЮ тему — от квантовой физики до мемов!"
                className="w-full p-3 bg-white/10 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Вопросов: {questionsCount}
              </label>
              <input
                type="range"
                min={5}
                max={10}
                value={questionsCount}
                onChange={(e) => setQuestionsCount(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                ⏱️ Время на ответ: {timePerQuestion} сек
              </label>
              <input
                type="range"
                min={10}
                max={120}
                step={5}
                value={timePerQuestion}
                onChange={(e) => setTimePerQuestion(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10 сек</span>
                <span>120 сек</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                📊 Показ результатов: {resultsDisplayTime} сек
              </label>
              <input
                type="range"
                min={3}
                max={30}
                value={resultsDisplayTime}
                onChange={(e) => setResultsDisplayTime(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>3 сек</span>
                <span>30 сек</span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Сложность</label>
              <div className="grid grid-cols-3 gap-2">
                {(['easy', 'medium', 'hard'] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={'py-2 px-4 rounded-lg font-medium transition-all ' +
                      (difficulty === d
                        ? 'bg-primary-500 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20')}
                  >
                    {d === 'easy' && '😊 Лёгкая'}
                    {d === 'medium' && '🤔 Средняя'}
                    {d === 'hard' && '🔥 Сложная'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="text-2xl">⚠️</div>
            <div className="flex-1">
              <div className="font-semibold text-red-300">
                {error === 'NO_GAMES_LEFT' && 'Игры закончились!'}
                {error === 'NOT_ENOUGH_PLAYERS' && 'Недостаточно игроков'}
                {error === 'TOPIC_NOT_ALLOWED' && '🚫 Тема запрещена'}
                {!['NO_GAMES_LEFT', 'NOT_ENOUGH_PLAYERS', 'TOPIC_NOT_ALLOWED'].includes(error) && 'Ошибка'}
              </div>
              <div className="text-sm text-red-200 mt-1">
                {error === 'NO_GAMES_LEFT' && (isVK() ? 'Купи игры за VK Голоса — окно покупки открыто.' : 'Купи игры в боте командой /buy')}
                {error === 'NOT_ENOUGH_PLAYERS' && 'Нужно минимум 2 игрока для старта'}
                {error === 'TOPIC_NOT_ALLOWED' && 'Эта тема не доступна для семейного режима. Выбери другую тему!'}
                {!['NO_GAMES_LEFT', 'NOT_ENOUGH_PLAYERS', 'TOPIC_NOT_ALLOWED'].includes(error) && error}
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-300 hover:text-red-100 text-xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {isHost ? (
          <button
            onClick={handleStart}
            disabled={!canStart || isStarting}
            className="w-full py-4 px-6 bg-green-500 text-white font-bold text-lg rounded-xl hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStarting ? '🤖 AI генерирует вопросы...' : 'Начать игру (' + readyCount + '/' + room.players.length + ' готовы)'}
          </button>
        ) : (
          <button
            onClick={handleReady}
            className={'w-full py-4 px-6 font-bold text-lg rounded-xl transition-all ' +
              (myPlayer?.isReady
                ? 'bg-green-500 text-white'
                : 'bg-white/20 text-white border-2 border-white/30')}
          >
            {myPlayer?.isReady ? '✓ Готов!' : 'Я готов'}
          </button>
        )}

        <button
          onClick={leaveRoom}
          className="w-full py-3 px-6 bg-white/10 text-gray-300 font-medium rounded-xl hover:bg-white/20 transition-all"
        >
          Выйти из комнаты
        </button>
      </div>

      {room.players.length < 1 && (
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>Поделись кодом <span className="text-primary-400 font-bold">{room.code}</span> с друзьями!</p>
        </div>
      )}
    </div>
  );
}